/**
 * AB#1754 — Calendar Settings section (C8 of Calendar v1 Epic AB#1746).
 *
 * Two cards + one save bar:
 *   Card 1 — calendar time settings (overlap, back-dating, week start, slot, time format).
 *   Card 2 — which booking statuses the salon uses.
 *
 * O3 (AB#2203 — SO-10 / SO-11 / SO-12 / SO-42 / SO-38): statuses use ONE owner-facing
 * vocabulary (lib/bookingLifecycle) and the design-system status colours, so owners
 * can no longer recolour statuses with hex values or rename them. Owners can still
 * switch optional statuses (Arrived, Started, No-show, Cancelled) on/off. The stored
 * colour / label values are sent back unchanged so the BE validator (exactly 7
 * rows) is satisfied and nothing else that reads them changes.
 *
 * Locked-status invariant (Booked + Completed cannot be disabled) is derived
 * client-side from `LOCKED_STATUS_KEYS`; the server validator also rejects it.
 * Save flow: one atomic PUT with the settings row + all 7 status rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
    useCalendarSettingsQuery,
    useUpdateCalendarSettingsMutation,
} from '../../../../hooks/useCalendarSettings';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getApiErrorMessage } from '../../../../types/api-errors';
import {
    bookingStatusColors,
    bookingStatusLabelKey,
    type LifecycleStatus,
} from '../../../../lib/bookingLifecycle';
import {
    AppointmentStatusKey,
    LOCKED_STATUS_KEYS,
    type CalendarSettingsResponse,
    type CalendarStatusSettingsDto,
    type UpdateCalendarSettingsRequest,
} from '../../../../types/api-responses';
// AB#1901 (W-10) — use the central boundary parser so the BE→FE
// AppointmentStatusKey conversion follows the same convention as the
// rest of the enum-converter sweep. Drops the previous local `BE_NAME_TO_KEY`
// map in favour of the shared `parseAppointmentStatusKey` (which handles
// the "Pending" → Booked rename + every other status).
import { parseAppointmentStatusKey } from '../../../../lib/enumParsers';
import c from './CalendarSettingsSection.module.css';

interface SettingsFormState {
    weekStart: number;
    timeSlotMinutes: number;
    timeFormat: number; // 12 | 24
    overlapAllowed: boolean;
    backdatingAllowed: boolean;
}

const SLOT_GRANULARITY_OPTIONS = [5, 10, 15, 30, 60] as const;

/** Sun-start week ordering matches the BE DayOfWeek=0..6. */
const WEEK_START_OPTIONS: Array<{ value: number; labelKey: string }> = [
    { value: 0, labelKey: 'cs.day.sunday' },
    { value: 1, labelKey: 'cs.day.monday' },
    { value: 6, labelKey: 'cs.day.saturday' },
];

/** Owner-facing statuses each settings row covers (the "Booked" row = Pending + Confirmed). */
const ROW_STATUSES: Record<number, LifecycleStatus[]> = {
    1: ['Pending', 'Approved'],
    6: ['Arrived'],
    7: ['Started'],
    3: ['Completed'],
    5: ['NoShow'],
    4: ['Cancelled'],
};

/** Plain-language hint per row. */
const ROW_HINT_KEYS: Record<number, string> = {
    1: 'cs.status.hint.booked',
    6: 'cs.status.hint.arrived',
    7: 'cs.status.hint.started',
    3: 'cs.status.hint.completed',
    5: 'cs.status.hint.noShow',
    4: 'cs.status.hint.cancelled',
};

/**
 * Display order matches mockup §I — 6 rows, no `Approved`. Owners cannot
 * configure the legacy `Approved` (StatusKey=2) status from this page;
 * its row remains in the form state so the PUT body still ships all 7
 * entries the BE validator requires.
 */
/**
 * AB#1901 (W-10) — Local component state shape. The wire DTO carries
 * `statusKey: AppointmentStatusKey | string` because the BE serializes the
 * enum as a string name; after intake normalization (see useEffect below)
 * every row's statusKey is the integer AppointmentStatusKey, so all the
 * downstream Map<AppointmentStatusKey, ...>, Set<AppointmentStatusKey>,
 * and find-by-key logic stays sound.
 */
type NormalizedStatusRow = Omit<CalendarStatusSettingsDto, 'statusKey'> & {
    statusKey: AppointmentStatusKey;
};

const STATUS_DISPLAY_ORDER: AppointmentStatusKey[] = [
    AppointmentStatusKey.Booked,
    AppointmentStatusKey.Arrived,
    AppointmentStatusKey.Started,
    AppointmentStatusKey.Completed,
    AppointmentStatusKey.NoShow,
    AppointmentStatusKey.Cancelled,
];

function buildSettingsForm(snapshot: CalendarSettingsResponse): SettingsFormState {
    return {
        weekStart: snapshot.weekStart,
        timeSlotMinutes: snapshot.timeSlotMinutes,
        timeFormat: snapshot.timeFormat,
        overlapAllowed: snapshot.overlapAllowed,
        backdatingAllowed: snapshot.backdatingAllowed,
    };
}

function settingsEqual(a: SettingsFormState, b: SettingsFormState): boolean {
    return (
        a.weekStart === b.weekStart &&
        a.timeSlotMinutes === b.timeSlotMinutes &&
        a.timeFormat === b.timeFormat &&
        a.overlapAllowed === b.overlapAllowed &&
        a.backdatingAllowed === b.backdatingAllowed
    );
}

function statusEqual(a: NormalizedStatusRow, b: NormalizedStatusRow): boolean {
    return (
        a.statusKey === b.statusKey &&
        a.enabled === b.enabled &&
        a.colorHex.toLowerCase() === b.colorHex.toLowerCase() &&
        a.labelEn === b.labelEn &&
        a.labelAr === b.labelAr
    );
}


/**
 * AB#1901 (W-10) — Coerce a BE-wire `statusKey` (string or number) into the
 * FE numeric `AppointmentStatusKey`. Delegates to the central
 * `parseAppointmentStatusKey` which knows the BE→FE "Pending"→Booked rename
 * + every other status. Falls back to `Booked` so the row still renders if
 * the BE adds an unknown status before the FE knows about it.
 */
function normalizeStatusKey(input: number | string): AppointmentStatusKey {
    return parseAppointmentStatusKey(input) ?? AppointmentStatusKey.Booked;
}

const CalendarSettingsSection: React.FC = () => {
    const { showToast } = useToast();
    const { t, language } = useLanguage();
    const { data, isLoading, isError, refetch } = useCalendarSettingsQuery();
    const mutation = useUpdateCalendarSettingsMutation();

    const [settingsForm, setSettingsForm] = useState<SettingsFormState | null>(null);
    const [statusRows, setStatusRows] = useState<NormalizedStatusRow[]>([]);

    // Seed local form whenever the server snapshot lands or refreshes.
    // CRITICAL: BE serializes the AppointmentStatus enum as a string
    // ("Pending", "Arrived", ...) via JsonStringEnumConverter. Normalize
    // each row's statusKey to the numeric AppointmentStatusKey so the
    // rest of the form (filtering, lookups, locked-status check) can
    // work uniformly with numbers.
    useEffect(() => {
        if (!data) return;
        setSettingsForm(buildSettingsForm(data));
        setStatusRows(
            data.statuses.map((r) => ({
                ...r,
                statusKey: normalizeStatusKey(r.statusKey),
            })),
        );
    }, [data]);

    // Rows actually rendered in the status table — filtered to mockup §I display set.
    const displayedRows = useMemo(() => {
        return STATUS_DISPLAY_ORDER
            .map((key) => statusRows.find((r) => r.statusKey === key))
            .filter((r): r is NormalizedStatusRow => !!r);
    }, [statusRows]);

    const isDirty = useMemo(() => {
        if (!data || !settingsForm) return false;
        if (!settingsEqual(settingsForm, buildSettingsForm(data))) return true;
        // Match the normalization applied to `statusRows` on intake — keys
        // here MUST be numeric for the Map.get(numeric) lookup to hit.
        const original = new Map(
            data.statuses.map((r) => {
                const normalized = {
                    ...r,
                    statusKey: normalizeStatusKey(r.statusKey),
                };
                return [normalized.statusKey, normalized];
            }),
        );
        return statusRows.some((r) => {
            const orig = original.get(r.statusKey);
            return !orig || !statusEqual(r, orig);
        });
    }, [data, settingsForm, statusRows]);

    const canSave = isDirty && !mutation.isPending;

    function updateSetting<K extends keyof SettingsFormState>(key: K, value: SettingsFormState[K]) {
        setSettingsForm((s) => (s ? { ...s, [key]: value } : s));
    }

    function updateStatusRow(statusKey: AppointmentStatusKey, patch: Partial<NormalizedStatusRow>) {
        setStatusRows((rows) =>
            rows.map((r) => (r.statusKey === statusKey ? { ...r, ...patch } : r)),
        );
    }

    function resetToDefaults() {
        if (!data) return;
        setSettingsForm(buildSettingsForm(data));
        setStatusRows(
            data.statuses.map((r) => ({
                ...r,
                statusKey: normalizeStatusKey(r.statusKey),
            })),
        );
    }

    async function save() {
        if (!settingsForm) return;
        // Ship ALL 7 rows (including the hidden Approved) so the BE validator
        // (exactly-7 invariant) is satisfied. Stored colour / label values are
        // returned untouched — owners only switch statuses on/off (O3).
        const body: UpdateCalendarSettingsRequest = {
            ...settingsForm,
            statuses: statusRows,
        };
        try {
            await mutation.mutateAsync(body);
            showToast(t('cs.toast.saved'), 'success');
        } catch (err) {
            // Server reasons are English-only: shown in English, localized text in Arabic.
            showToast(language === 'en' ? getApiErrorMessage(err, t('cs.toast.saveFailed')) : t('cs.toast.saveFailed'), 'error');
        }
    }

    // ── Intro block — rendered identically across loading / error / loaded
    //    so the page doesn't shift while the settings query hydrates.
    const introBlock = (
        <div className={c.intro}>
            <h1>{t('cs.title')}</h1>
            <p>{t('cs.subtitle')}</p>
        </div>
    );

    if (isLoading || !settingsForm) {
        return (
            <div className={c.page}>
                <div className={c.shell}>
                    {introBlock}
                    <div className={c.skeleton} aria-hidden="true" />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className={c.page}>
                <div className={c.shell}>
                    {introBlock}
                    <div className={c.errorState}>
                        <span>{t('cs.loadError')}</span>
                        <button type="button" className={c.btnGhost} onClick={() => refetch()}>
                            {t('cs.retry')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={c.page}>
            <div className={c.shell}>
                {introBlock}

                {/* ── Card 1: Calendar Time settings ─────────────────────── */}
                <div className={c.card}>
                    <div className={c.cardHead}>
                        <div>
                            <h2>{t('cs.time.title')}</h2>
                            <p>{t('cs.time.subtitle')}</p>
                        </div>
                        <div className={c.cardActions}>
                            <div className={c.cardActionsToggle}>
                                <span id="cs-overlap-label">{t('cs.overlap.label')}</span>
                                <button
                                    type="button"
                                    className={`${c.toggleBtn} ${settingsForm.overlapAllowed ? c.toggleBtnOn : ''}`}
                                    aria-pressed={settingsForm.overlapAllowed}
                                    aria-labelledby="cs-overlap-label"
                                    onClick={() => updateSetting('overlapAllowed', !settingsForm.overlapAllowed)}
                                />
                            </div>
                            <div className={c.cardActionsToggle}>
                                <span id="cs-backdating-label">{t('cs.backdating.label')}</span>
                                <button
                                    type="button"
                                    className={`${c.toggleBtn} ${settingsForm.backdatingAllowed ? c.toggleBtnOn : ''}`}
                                    aria-pressed={settingsForm.backdatingAllowed}
                                    aria-labelledby="cs-backdating-label"
                                    onClick={() => updateSetting('backdatingAllowed', !settingsForm.backdatingAllowed)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={c.fieldGrid}>
                        <div className={c.field}>
                            <label htmlFor="cs-week-start">{t('cs.weekStart')}</label>
                            <select
                                id="cs-week-start"
                                className={c.select}
                                value={settingsForm.weekStart}
                                onChange={(e) => updateSetting('weekStart', Number(e.target.value))}
                            >
                                {WEEK_START_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                                ))}
                            </select>
                        </div>

                        <div className={c.field}>
                            <label htmlFor="cs-time-slot">{t('cs.timeSlot')}</label>
                            <select
                                id="cs-time-slot"
                                className={c.select}
                                value={settingsForm.timeSlotMinutes}
                                onChange={(e) => updateSetting('timeSlotMinutes', Number(e.target.value))}
                            >
                                {SLOT_GRANULARITY_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{t('cs.minutes').replace('{n}', String(m))}</option>
                                ))}
                            </select>
                        </div>

                        <div className={c.field}>
                            <label htmlFor="cs-time-format">{t('cs.timeFormat')}</label>
                            <select
                                id="cs-time-format"
                                className={c.select}
                                value={settingsForm.timeFormat}
                                onChange={(e) => updateSetting('timeFormat', Number(e.target.value))}
                            >
                                <option value={12}>{t('cs.timeFormat.12')}</option>
                                <option value={24}>{t('cs.timeFormat.24')}</option>
                            </select>
                        </div>
                    </div>

                    <div className={c.note}>
                        <strong>{t('cs.overlap.label')}</strong> — {t('cs.overlap.help')}<br />
                        <strong>{t('cs.backdating.label')}</strong> — {t('cs.backdating.help')}
                    </div>
                </div>

                {/* ── Card 2: booking statuses the salon uses (O3) ───────── */}
                <div className={c.card}>
                    <div className={`${c.cardHead} ${c.cardHeadNoBorder}`}>
                        <div>
                            <h2>{t('cs.status.title')}</h2>
                            <p>{t('cs.status.subtitle')}</p>
                        </div>
                    </div>

                    <ul className={c.statusList} aria-label={t('cs.status.title')}>
                        {displayedRows.map((row) => {
                            const isLocked = LOCKED_STATUS_KEYS.has(row.statusKey);
                            const statuses = ROW_STATUSES[row.statusKey] ?? [];
                            const names = statuses.map((st) => t(bookingStatusLabelKey(st))).join(' · ');
                            const labelId = `cs-status-${row.statusKey}`;
                            return (
                                <li
                                    key={row.statusKey}
                                    className={`${c.statusItem} ${!row.enabled ? c.rowDisabled : ''}`}
                                    data-testid={`cs-status-row-${row.statusKey}`}
                                >
                                    <div className={c.statusText}>
                                        <span className={c.statusName} id={labelId}>
                                            {statuses.map((st) => (
                                                <span
                                                    key={st}
                                                    className={c.statusDot}
                                                    style={{ background: bookingStatusColors(st).fg }}
                                                    aria-hidden="true"
                                                />
                                            ))}
                                            {names}
                                        </span>
                                        <span className={c.statusHint}>
                                            {t(ROW_HINT_KEYS[row.statusKey] ?? '')}
                                            {isLocked && <> {t('cs.status.alwaysOn')}</>}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`${c.toggleBtn} ${row.enabled ? c.toggleBtnOn : ''}`}
                                        aria-pressed={row.enabled}
                                        aria-labelledby={labelId}
                                        disabled={isLocked}
                                        onClick={() => updateStatusRow(row.statusKey, { enabled: !row.enabled })}
                                    />
                                </li>
                            );
                        })}
                    </ul>

                    <div className={c.note}>{t('cs.status.note')}</div>
                </div>

                {/* ── Save bar ───────────────────────────────────────────── */}
                <div className={c.saveBar}>
                    <button
                        type="button"
                        className={c.btnGhost}
                        disabled={!isDirty || mutation.isPending}
                        onClick={resetToDefaults}
                    >
                        {t('cs.discard')}
                    </button>
                    <button
                        type="button"
                        className={c.btnPrimary}
                        disabled={!canSave}
                        onClick={save}
                    >
                        {mutation.isPending ? t('cs.saving') : t('cs.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CalendarSettingsSection;
